/**
 * Admin dashboard: lists model report submissions with filters,
 * search and pagination. Customer base price is set automatically from default_customer_price;
 * overrides are managed via Price Management. Status is Payment Pending / Paid / Refunded.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  HomeIcon,
  EnvelopeIcon,
  ClockIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import logo from '../assets/Grey Imports Logo.png'; // Import the logo
import { useAuth, supabase } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { convertToMelbourneDate } from '../utils/dateUtils';
import Footer from './Footer';
import AdminLayout from './AdminLayout';
import ArchiveConfirmModal from './archive/ArchiveConfirmModal';
import RefundConfirmModal from './refund/RefundConfirmModal';
import StatusToast from './StatusToast';
import { archiveOrder, canArchiveOrderStatus } from '../utils/archiveOrderApi';
import { canRefundOrderStatus, refundOrder } from '../utils/refundOrderApi';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Navbar features for the admin sidebar
const adminFeatureLinks = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Overview',
    icon: HomeIcon,
    type: 'button'
  },
  {
    id: 'send-email',
    label: 'Send Marketing Email',
    description: 'Marketing',
    icon: EnvelopeIcon,
    type: 'link',
    to: '/send-marketing-email'
  },
  {
    id: 'sevs-expiry',
    label: 'SEVS Expiry Management',
    description: 'Management',
    icon: ClockIcon,
    type: 'link',
    to: '/sevs-expiry'
  },
  {
    id: 'model-report-list',
    label: 'Model Report List',
    description: 'Database',
    icon: DocumentTextIcon,
    type: 'link',
    to: '/admin/model-reports',
    state: { from: '/admin' }
  },
  {
    id: 'add-model-report',
    label: 'Add New Model Report',
    description: 'Create',
    icon: PlusCircleIcon,
    type: 'link',
    to: '/add-new-customer-model-reports'
  },
  {
    id: 'verify-uid',
    label: 'U.ID Verification ',
    description: 'Validation',
    icon: ShieldCheckIcon,
    type: 'button'
  },
  {
    id: 'view-analysis',
    label: 'View Analysis',
    description: 'Insights',
    icon: ChartBarIcon,
    type: 'group',
    children: [
      {
        id: 'order-analytics',
        label: 'Order Analytics',
        to: '/view-analysis',
        state: { analysisView: 'orders' },
      },
      {
        id: 'revenue-analytics',
        label: 'Revenue Analytics',
        to: '/view-analysis',
        state: { analysisView: 'revenue' },
      },
      {
        id: 'performance-overview',
        label: 'Performance Overview',
        to: '/view-analysis',
        state: { analysisView: 'performance' },
      },
    ],
    // Analytics exposes revenue, so it is owner-only.
    roles: ['owner']
  }
];

// Payment status is free text. Refund labels vary by case and spacing:
// "Refunded-10/03/2026", "Refunded- 27/01/2026", "REFUNDED-24/09/2026",
// "REFUNDED- 27/08/2026", plus "refund" / "refunds". Order Deleted is included
// until those rows are archived in production, so pending + paid + refund = total.
const isRefundedStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized.includes('refund') || normalized === 'order deleted';
};

const applyRefundedStatusFilter = (query) =>
  query.or('status.ilike.%refund%,status.ilike.order deleted');

const AdminPortal = () => {
  const [submissions, setSubmissions] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [searchBy, setSearchBy] = useState(''); // Default search field
  const [dateFilter, setDateFilter] = useState('all'); // Add this line
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [userName, setUserName] = useState('Admin');
  const dropdownRef = useRef(null);
  const { user, signOut, isOwner } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [submissionsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [priceBreakdowns, setPriceBreakdowns] = useState({}); // { [submissionId]: { loading, error, data } }
  const [stateFilter, setStateFilter] = useState('all'); // Add this line for state filter
  const [isLoading, setIsLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    paymentPending: 0,
    paid: 0,
    refunded: 0
  });
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hoveredSidebarItem, setHoveredSidebarItem] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [isRefunding, setIsRefunding] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const latestFetchIdRef = useRef(0);
  const latestStatusCountFetchIdRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchUserDetails(user.id);
      fetchSubmissions();
    }
  }, [user]);

  // Close the profile dropdown on any click landing outside it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    const debounceDelay = searchBy ? 650 : 0;

    setIsSearchPending(Boolean(searchBy) && trimmedSearchTerm !== debouncedSearchTerm);

    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, debounceDelay);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchBy, debouncedSearchTerm]);

  const applyColumnSearchFilter = (query, selectedField, rawSearchTerm) => {
    const normalizedSearchTerm = rawSearchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm || !selectedField) {
      return query;
    }

    switch (selectedField) {
      case 'rawNumber':
        return query.ilike('user_raw', `%${normalizedSearchTerm}%`);
      case 'mreApproval':
        return query.ilike('mr_approval', `%${normalizedSearchTerm}%`);
      case 'bodyType':
        return query.ilike('bodytype', `%${normalizedSearchTerm}%`);
      case 'chassisCode':
        return query.ilike('chassis_code', `%${normalizedSearchTerm}%`);
      case 'sevsEntry':
        return query.ilike('sevs_entry', `%${normalizedSearchTerm}%`);
      case 'name':
        return query.ilike('user_name', `%${normalizedSearchTerm}%`);
      case 'make':
        return query.ilike('car_make', `%${normalizedSearchTerm}%`);
      case 'model':
        return query.ilike('car_model', `%${normalizedSearchTerm}%`);
      case 'vin':
        return query.ilike('vin_number', `%${normalizedSearchTerm}%`);
      case 'uniqueId':
        return query.ilike('customer_unique_id', `%${normalizedSearchTerm}%`);
      default:
        return query;
    }
  };

  // Resolve the signed-in admin's display name for the header; failure leaves the 'Admin' default.
  const fetchUserDetails = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', userId)
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setUserName(data[0].name);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirmation(false);
    await handleLogout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirmation(false);
  };

  // Pass the origin in route state so the VIN screen can send the admin back to the dashboard.
  const handleVerifyVin = () => {
    navigate('/verify-vin', { state: { from: '/admin' } });
  };

  const handleLogoClick = () => {
    window.location.reload();
  };

  const showToast = (title, message, tone = 'success') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ title, message, tone });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 7000);
  };

  const handleArchiveClick = (submission) => {
    if (!canArchiveOrderStatus(submission.status)) return;
    setArchiveTarget(submission);
  };

  const handleArchiveConfirm = async (reason) => {
    if (!archiveTarget?.id) return;
    setIsArchiving(true);
    try {
      await archiveOrder(archiveTarget.id, reason);
      setArchiveTarget(null);
      showToast('Order archived!', 'Moved to Archived Orders. VIN is free for new orders.');
      await Promise.all([fetchSubmissions(), fetchStatusCounts()]);
    } catch (err) {
      showToast('Archive failed!', err.message || 'Could not archive order', 'error');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRefundClick = (submission) => {
    if (!canRefundOrderStatus(submission.status)) return;
    setRefundTarget(submission);
  };

  const handleRefundConfirm = async () => {
    if (!refundTarget?.id) return;
    setIsRefunding(true);
    try {
      await refundOrder(refundTarget.id, refundTarget.chassisvin);
      const refundedId = refundTarget.id;
      setRefundTarget(null);
      setExpandedRow((current) => (current === refundedId ? null : current));
      showToast('Marked as refunded', 'Complete the refund in Stripe.');
      await Promise.all([fetchSubmissions(), fetchStatusCounts()]);
    } catch (err) {
      showToast('Refund update failed', err.message || 'Could not update the order', 'error');
    } finally {
      setIsRefunding(false);
    }
  };

  const handleSidebarAction = (featureId) => {
    // Already on the dashboard, so "Dashboard" just returns to the top of the list.
    if (featureId === 'dashboard') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsSidebarOpen(false);
      return;
    }

    if (featureId === 'verify-uid') {
      handleVerifyVin();
      setIsSidebarOpen(false);
    }
  };

  // Fetch submissions with pagination
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [statusFilter, debouncedSearchTerm, searchBy, dateFilter, stateFilter]);

  useEffect(() => {
    fetchSubmissions();
    fetchStatusCounts();
  }, [currentPage, statusFilter, debouncedSearchTerm, searchBy, dateFilter, stateFilter]);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [statusFilter, currentPage]);

  useEffect(() => {
    const testServices = async () => {
      try {
        // Test Supabase connection
        const { data, error } = await supabase
          .from('modelreports')
          .select('id')
          .limit(1);

        if (error) {
          console.error('Supabase test error:', error);
        } else {
          console.log('Connection successful');
        }
      } catch (err) {
        console.error('Service test failed:', err);
      }
    };

    testServices();
  }, []);

  const buildFilterQuery = (query, { includeStatus = true } = {}) => {
    // Active portal never shows archived orders (see /archived-orders).
    query = query.eq('is_archived', false);

    if (includeStatus) {
      if (statusFilter === 'refunded') {
        query = applyRefundedStatusFilter(query);
      } else if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
    }
    
    // Apply state filter
    if (stateFilter !== 'all') {
      query = query.eq('state', stateFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      switch (dateFilter) {
        case 'today': {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          query = query.gte('created_at', today.toISOString())
                       .lt('created_at', tomorrow.toISOString());
          break;
        }
        case 'yesterday': {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          query = query.gte('created_at', yesterday.toISOString())
                       .lt('created_at', today.toISOString());
          break;
        }
        case 'week': {
          const dayOfWeek = today.getDay();
          const lastSunday = new Date(today);
          lastSunday.setDate(today.getDate() - dayOfWeek);
          lastSunday.setHours(0, 0, 0, 0);
          const lastMonday = new Date(lastSunday);
          lastMonday.setDate(lastSunday.getDate() - 6);
          query = query.gte('created_at', lastMonday.toISOString())
                       .lte('created_at', lastSunday.toISOString());
          break;
        }
        case 'month': {
          const firstDayOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDayOfLastMonth = new Date(firstDayOfThisMonth - 1);
          lastDayOfLastMonth.setHours(23, 59, 59, 999);
          const firstDayOfLastMonth = new Date(lastDayOfLastMonth.getFullYear(), lastDayOfLastMonth.getMonth(), 1);
          query = query.gte('created_at', firstDayOfLastMonth.toISOString())
                       .lte('created_at', lastDayOfLastMonth.toISOString());
          break;
        }
        case '3months': {
          const firstDayOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDayOfLastMonth = new Date(firstDayOfThisMonth - 1);
          lastDayOfLastMonth.setHours(23, 59, 59, 999);
          const firstDayOf3MonthsAgo = new Date(firstDayOfThisMonth);
          firstDayOf3MonthsAgo.setMonth(firstDayOf3MonthsAgo.getMonth() - 3);
          query = query.gte('created_at', firstDayOf3MonthsAgo.toISOString())
                       .lte('created_at', lastDayOfLastMonth.toISOString());
          break;
        }
        case '6months': {
          const firstDayOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDayOfLastMonth = new Date(firstDayOfThisMonth - 1);
          lastDayOfLastMonth.setHours(23, 59, 59, 999);
          const firstDayOf6MonthsAgo = new Date(firstDayOfThisMonth);
          firstDayOf6MonthsAgo.setMonth(firstDayOf6MonthsAgo.getMonth() - 6);
          query = query.gte('created_at', firstDayOf6MonthsAgo.toISOString())
                       .lte('created_at', lastDayOfLastMonth.toISOString());
          break;
        }
        case 'year': {
          const lastYear = today.getFullYear() - 1;
          const firstDayOfLastYear = new Date(lastYear, 0, 1);
          const lastDayOfLastYear = new Date(lastYear, 11, 31, 23, 59, 59, 999);
          query = query.gte('created_at', firstDayOfLastYear.toISOString())
                       .lte('created_at', lastDayOfLastYear.toISOString());
          break;
        }
      }
    }

    // Apply search filter
    if (debouncedSearchTerm && searchBy) {
      query = applyColumnSearchFilter(query, searchBy, debouncedSearchTerm);
    }

    return query;
  };



  // Badge counts ignore the status card filter so each card keeps its own total.
  // Counts are exact, not a downloaded page, so they stay correct past 1,000 rows.
  const fetchStatusCounts = async () => {
    const fetchId = ++latestStatusCountFetchIdRef.current;
    try {
      const countQuery = () =>
        buildFilterQuery(
          supabase.from('admin_submissions_view').select('id', { count: 'exact', head: true }),
          { includeStatus: false }
        );

      const [totalResult, pendingResult, paidResult, refundedResult] = await Promise.all([
        countQuery(),
        countQuery().eq('status', 'Payment Pending'),
        countQuery().eq('status', 'Paid'),
        applyRefundedStatusFilter(countQuery()),
      ]);

      if (fetchId !== latestStatusCountFetchIdRef.current) {
        return;
      }

      const error = totalResult.error || pendingResult.error || paidResult.error || refundedResult.error;
      if (error) {
        console.error('Status count fetch error:', error);
        return;
      }

      setStatusCounts({
        total: totalResult.count || 0,
        paymentPending: pendingResult.count || 0,
        paid: paidResult.count || 0,
        refunded: refundedResult.count || 0,
      });
    } catch (error) {
      console.error('Error fetching status counts:', error);
    }
  };

  // Loads one page of submissions from the admin view, with filters applied server-side.
  // Ignores late responses if the user changed page/filter while this request was in flight.
  const fetchSubmissions = async () => {
    const fetchId = ++latestFetchIdRef.current;
    setIsLoading(true);
    try {
      let dataQuery = supabase
        .from('admin_submissions_view')
        .select('*')
        .order('id', { ascending: false });

      dataQuery = buildFilterQuery(dataQuery);
      
      const offset = (currentPage - 1) * submissionsPerPage;
      dataQuery = dataQuery.range(offset, offset + submissionsPerPage - 1);
      
      const { data, error } = await dataQuery;

      if (fetchId !== latestFetchIdRef.current) {
        return;
      }

      if (error) {
        console.error('Fetch error:', error);
        throw error;
      }
      let transformedData = (data || []).map(item => ({
        id: item.id,
        customer_unique_id: item.customer_unique_id,
        rawapprovalnumber: item.user_raw || '',
        name: item.user_name || '',
        email: item.user_email || '',
        phone: '+' + item.user_phone || '',
        mr_id: item.mr_id,
        todaysdate: convertToMelbourneDate(item.created_at),
        carmake: item.car_make || '',
        carmodel: item.car_model || '',
        baseprice: item.customer_price ? `$${item.customer_price}` : 'Needs to be set',
        builddate: item.build_date,
        chassis_code: item.chassis_code || '',
        chassisvin: item.vin_number,
        state: item.state,
        avvapprovalnumber: item.avv_approval_number,
        avvemail: item.avv_approval_email,
        mreused: item.mr_approval || '',
        status: item.status || 'Processing',
        user_id: item.user_id,
        vehicle_type: (item.vehicle_type || item.car_type || '').trim() || '-',
        transactions: item.transaction_id ? [{
          id: item.transaction_id,
          price: item.transaction_price,
          payment_status: item.payment_status,
          payment_date: item.payment_timestamp ? convertToMelbourneDate(item.payment_timestamp) : '',
          payment_method: item.payment_method,
          reference_no: item.reference_no,
          invoice_number: item.invoice_number
        }] : []
      }));

      // Get total count for pagination
      let countQuery = supabase
        .from('admin_submissions_view')
        .select('*', { count: 'exact', head: true });
      
      countQuery = buildFilterQuery(countQuery);
      const { count } = await countQuery;

      if (fetchId !== latestFetchIdRef.current) {
        return;
      }
      
      setTotalCount(count || 0);
      setTotalPages(Math.ceil((count || 0) / submissionsPerPage));

      setSubmissions(transformedData);
    } catch (error) {
      if (fetchId !== latestFetchIdRef.current) {
        return;
      }
      console.error('Error fetching submissions:', error);
      alert('Error loading submissions. Please try again.');
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setIsLoading(false);
        setIsSearchPending(false);
      }
    }
  };

  // Update pagination to work with server-side data
  const indexOfLastSubmission = currentPage * submissionsPerPage;
  const indexOfFirstSubmission = indexOfLastSubmission - submissionsPerPage;

  // Fetch price breakdown from backend when expanding a row
  const fetchPriceBreakdown = async (submissionId) => {
    setPriceBreakdowns(prev => ({
      ...prev,
      [submissionId]: { loading: true, error: null, data: null }
    }));
    try {
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-price-breakdown?submissionId=${submissionId}`);
      if (!res.ok) throw new Error('Failed to fetch price breakdown');
      const data = await res.json();
      setPriceBreakdowns(prev => ({
        ...prev,
        [submissionId]: { loading: false, error: null, data }
      }));
    } catch (err) {
      setPriceBreakdowns(prev => ({
        ...prev,
        [submissionId]: { loading: false, error: err.message, data: null }
      }));
    }
  };

  // Expands one row at a time and loads its price breakdown when opening.
  const toggleExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
    if (expandedRow !== id) {
      fetchPriceBreakdown(id);
    }
  };

  const StatusBadge = ({ status, transactions }) => {
    // Legacy "Verification Pending" (pre auto-price) displays as Processing.
    const displayStatus =
      status === 'Verification Pending' ? 'Processing' : status;

    let bgColor = 'bg-slate-100 text-slate-600';
    let text = displayStatus;

    if (displayStatus === 'Verified') {
      bgColor = 'bg-sky-100 text-sky-700';
    } else if (displayStatus === 'Payment Pending') {
      bgColor = 'bg-violet-100 text-violet-700';
      text = 'Payment Pending';
    } else if (displayStatus === 'Paid') {
      bgColor = 'bg-emerald-100 text-emerald-700';
      text = 'Paid';
    } else if (displayStatus === 'Processing') {
      bgColor = 'bg-slate-100 text-slate-600';
      text = 'Processing';
    } else if (isRefundedStatus(displayStatus)) {
      bgColor = 'bg-rose-100 text-rose-700';
    }

    return (
      <div className="space-y-1">
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${bgColor}`}>
          {text}
        </span>
        {displayStatus === 'Paid' && transactions?.[0] && (
          <p className="text-xs text-gray-500">
            Paid on {transactions[0].payment_date}
          </p>
        )}
      </div>
    );
  };

  const PaginationControls = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{indexOfFirstSubmission + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(indexOfLastSubmission, totalCount)}
              </span> of{' '}
              <span className="font-medium">{totalCount}</span> results
            </p>
          </div>
          <div>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-l-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    1
                  </button>
                  {startPage > 2 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">
                      ...
                    </span>
                  )}
                </>
              )}

              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => setCurrentPage(number)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                    currentPage === number
                      ? 'z-10 bg-blue-100 border-blue-600 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {number}
                </button>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">
                      ...
                    </span>
                  )}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-r-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const SidebarNavItem = ({ item, compact = false }) => {
    const Icon = item.icon;
    const isActive = item.id === 'dashboard' || hoveredSidebarItem === item.id;
    const canShowItem = !item.roles || item.roles.includes(isOwner ? 'owner' : 'admin');

    if (!canShowItem) {
      return null;
    }

    const baseClassName = compact
      ? `group relative flex h-11 w-11 items-center justify-center rounded-2xl border transition-all duration-300 ease-out ${isActive ? 'border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-200/60' : 'border-slate-200 bg-white text-blue-600 hover:border-blue-600 hover:bg-blue-50'}`
      : `group relative flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-300 ease-out ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-200/60' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'}`;

    const labelRailClassName = compact
      ? 'pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 opacity-0 shadow-xl shadow-slate-200/70 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100'
      : 'pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 opacity-0 shadow-xl shadow-slate-200/70 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100';

    const content = (
      <>
        <span className={compact ? '' : 'flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-white/20 group-hover:text-white'}>
          <Icon className="h-5 w-5" />
        </span>
        {!compact && (
          <span className="min-w-0">
            <span className="block truncate">{item.label}</span>
            <span className="block text-xs font-normal text-slate-500 group-hover:text-blue-100">
              {item.description}
            </span>
          </span>
        )}
      </>
    );

    if (item.type === 'link') {
      return (
        <Link
          to={item.to}
          state={item.state}
          className={baseClassName}
          onClick={() => setIsSidebarOpen(false)}
          onMouseEnter={() => setHoveredSidebarItem(item.id)}
          onMouseLeave={() => setHoveredSidebarItem(null)}
          onFocus={() => setHoveredSidebarItem(item.id)}
          onBlur={() => setHoveredSidebarItem(null)}
        >
          {content}
          {compact && <span className={labelRailClassName}>{item.label}</span>}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={() => handleSidebarAction(item.id)}
        className={baseClassName}
        onMouseEnter={() => setHoveredSidebarItem(item.id)}
        onMouseLeave={() => setHoveredSidebarItem(null)}
        onFocus={() => setHoveredSidebarItem(item.id)}
        onBlur={() => setHoveredSidebarItem(null)}
      >
        {content}
        {compact && <span className={labelRailClassName}>{item.label}</span>}
      </button>
    );
  };

  return (
    <AdminLayout
      activeItemId="dashboard"
      title="Admin Dashboard"
      subtitle="View and manage submissions and administrative operations."
      contentClassName="w-full max-w-none"
      footerContainerClassName="mt-10 lg:mt-14"
    >
          <div className="mb-6 hidden gap-4 sm:grid sm:grid-cols-2 xl:grid-cols-4">
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${statusFilter === 'all' ? 'border-blue-700 bg-blue-600 text-white shadow-lg shadow-blue-200/60 ring-1 ring-blue-200/70 scale-[1.01]' : 'border-blue-200 bg-white hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${statusFilter === 'all' ? 'text-white' : 'text-slate-500'}`}>Total</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${statusFilter === 'all' ? 'text-white' : 'text-slate-900'}`}>{statusCounts.total}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('Payment Pending')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${statusFilter === 'Payment Pending' ? 'border-violet-300 bg-violet-100 text-violet-900 shadow-lg shadow-violet-100/80 ring-1 ring-violet-200/80 scale-[1.01]' : 'border-violet-200 bg-white hover:border-violet-300 hover:bg-violet-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${statusFilter === 'Payment Pending' ? 'text-violet-900' : 'text-slate-500'}`}>Payment Pending</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${statusFilter === 'Payment Pending' ? 'text-violet-900' : 'text-violet-600'}`}>{statusCounts.paymentPending}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('Paid')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${statusFilter === 'Paid' ? 'border-emerald-300 bg-emerald-100 text-emerald-900 shadow-lg shadow-emerald-100/80 ring-1 ring-emerald-200/80 scale-[1.01]' : 'border-emerald-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${statusFilter === 'Paid' ? 'text-emerald-900' : 'text-slate-500'}`}>Paid</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${statusFilter === 'Paid' ? 'text-emerald-900' : 'text-emerald-600'}`}>{statusCounts.paid}</p>
            </button>
            <button
              type="button"
              onClick={() => setStatusFilter('refunded')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${statusFilter === 'refunded' ? 'border-rose-300 bg-rose-100 text-rose-900 shadow-lg shadow-rose-100/80 ring-1 ring-rose-200/80 scale-[1.01]' : 'border-rose-200 bg-white hover:border-rose-300 hover:bg-rose-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${statusFilter === 'refunded' ? 'text-rose-900' : 'text-slate-500'}`}>Refunded</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${statusFilter === 'refunded' ? 'text-rose-900' : 'text-rose-600'}`}>{statusCounts.refunded}</p>
            </button>
          </div>

          <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="grid flex-1 gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.8fr)]">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={searchBy ? 'Search submissions...' : 'Select a field to search...'}
                    className={`w-full rounded-2xl border px-11 py-3 text-sm shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 ${!searchBy ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500' : 'border-slate-300 bg-white text-slate-900'}`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={!searchBy}
                  />
                  <svg
                    className={`absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 ${!searchBy ? 'text-slate-300' : 'text-slate-400'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchBy && isSearchPending && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
                    </div>
                  )}
                </div>

                <select
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={searchBy}
                  onChange={(e) => {
                    setSearchBy(e.target.value);
                    if (e.target.value !== searchBy) {
                      setSearchTerm('');
                    }
                  }}
                >
                  <option value="">Select Search Field</option>
                  <option value="rawNumber">Raw Number</option>
                  <option value="uniqueId">Unique ID</option>
                  <option value="name">Name</option>
                  <option value="make">Make</option>
                  <option value="model">Model</option>
                  <option value="vin">Vin</option>
                  <option value="bodyType">Body Type</option>
                  <option value="chassisCode">Chassis Code</option>
                  <option value="sevsEntry">SEVS Entry</option>
                  <option value="mreApproval">MRE Approval</option>
                </select>

                <select
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="all">All Dates</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="year">Last Year</option>
                </select>

                <select
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value)}
                >
                  <option value="all">All States</option>
                  <option value="NSW">NSW</option>
                  <option value="VIC">VIC</option>
                  <option value="QLD">QLD</option>
                  <option value="WA">WA</option>
                  <option value="SA">SA</option>
                  <option value="TAS">TAS</option>
                  <option value="ACT">ACT</option>
                  <option value="NT">NT</option>
                </select>
              </div>
            </div>

            <div className="mt-4 sm:hidden">
              <select
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Payment Pending">Payment Pending</option>
                <option value="Paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10"></th>
                <th className="hidden md:table-cell py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                <th className="hidden md:table-cell py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RAW Number</th>
                <th className="hidden md:table-cell py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UNIQUE ID</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Car Details</th>
                <th className="hidden xl:table-cell py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading submissions...</p>
                    </div>
                  </td>
                </tr>
              ) : submissions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12">
                    <div className="text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm 
                          ? "No results match your search criteria" 
                          : statusFilter === 'refunded'
                            ? 'No refunded submissions'
                          : statusFilter !== 'all'
                            ? `No submissions with status "${statusFilter}"`
                            : "No submissions available yet"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => (
                  <React.Fragment key={`submission-${submission.id}`}>
                    <tr className="hover:bg-gray-50">
                      <td
                        className="py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        <button
                          onClick={() => toggleExpand(submission.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {expandedRow === submission.id ? 
                            <ChevronUpIcon className="h-5 w-5" /> : 
                            <ChevronDownIcon className="h-5 w-5" />
                          }
                        </button>
                      </td>
                      <td
                        className="hidden md:table-cell py-4 px-4 whitespace-nowrap text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {submission.todaysdate}
                      </td>
                      <td
                        className="hidden md:table-cell py-4 px-4 whitespace-nowrap text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {submission.rawapprovalnumber}
                      </td>
                      <td
                        className="hidden md:table-cell py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {submission.customer_unique_id}
                      </td>
                      <td
                        className="py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {submission.name}
                      </td>
                      <td
                        className="py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {`${submission.carmake} ${submission.carmodel}`}
                      </td>
                      <td
                        className="hidden xl:table-cell py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        {submission.vehicle_type}
                      </td>
                      <td
                        className="py-4 px-4 text-xs min-[425px]:text-sm lg:text-base cursor-pointer"
                        onClick={() => toggleExpand(submission.id)}
                      >
                        <StatusBadge 
                          status={submission.status} 
                          transactions={submission.transactions} 
                        />
                      </td>
                    </tr>
                    {expandedRow === submission.id && (
                      <tr className="bg-slate-50/70">
                        <td colSpan="8" className="px-5 py-5 lg:px-6">
                          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.35)]">
                            <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2 lg:p-5">
                              <div className="rounded-xl border border-slate-200 bg-sky-50/80">
                                <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                  <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-blue-700 flex items-center gap-2">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    User Details
                                  </h3>
                                </div>
                                <div className="grid gap-6 px-4 py-4 2xl:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]">
                                  <div className="space-y-4">
                                    <div>
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Email</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">{submission.email || '-'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Phone</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.phone || '-'}</div>
                                    </div>
                                    <div className="md:hidden">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">RAW Number</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.rawapprovalnumber || '-'}</div>
                                    </div>
                                    <div>
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Base Price</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm font-medium text-slate-900">{submission.baseprice}</div>
                                    </div>
                                  </div>

                                  <div>
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Price Breakdown</div>
                                    <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                                      {submission.baseprice === 'Needs to be set' ? (
                                        <div className="text-xs min-[425px]:text-sm text-slate-500">Default price will apply on next login/order</div>
                                      ) : (() => {
                                        const pb = priceBreakdowns[submission.id];
                                        if (!pb || pb.loading) return <div className="text-xs min-[425px]:text-sm text-slate-500">Loading breakdown...</div>;
                                        if (pb.error) return <div className="text-xs min-[425px]:text-sm text-red-500">{pb.error}</div>;
                                        const { customerPrice, typePrice, sevsFee, stripeFee, beforeCapPrice, adjustment } = pb.data || {};
                                        const total = beforeCapPrice !== undefined && adjustment !== undefined
                                          ? beforeCapPrice - adjustment
                                          : undefined;
                                        return (
                                          <>
                                            <div className="space-y-2 text-xs min-[425px]:text-sm text-slate-700">
                                              <div className="flex items-center justify-between gap-4"><span>Customer Price</span><span className="font-medium text-slate-900">${customerPrice !== undefined ? customerPrice.toFixed(2) : '-'}</span></div>
                                              <div className="flex items-center justify-between gap-4"><span>Type Price</span><span className="font-medium text-slate-900">${typePrice !== undefined ? typePrice.toFixed(2) : '-'}</span></div>
                                              <div className="flex items-center justify-between gap-4"><span>SEVS</span><span className="font-medium text-slate-900">${sevsFee !== undefined ? sevsFee.toFixed(2) : '-'}</span></div>
                                              <div className="flex items-center justify-between gap-4"><span>Processing Fee</span><span className="font-medium text-slate-900">${stripeFee !== undefined ? stripeFee.toFixed(2) : '-'}</span></div>
                                              <div className="flex items-center justify-between gap-4"><span>Before Cap</span><span className="font-medium text-slate-900">${beforeCapPrice !== undefined ? beforeCapPrice.toFixed(2) : '-'}</span></div>
                                              {adjustment > 0 && (
                                                <div className="flex items-center justify-between gap-4"><span>Cap Adjustment</span><span className="font-medium text-slate-900">-${adjustment.toFixed(2)}</span></div>
                                              )}
                                            </div>
                                            <div className="mt-4 flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100">
                                              <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-base font-semibold text-blue-700">
                                                  $
                                                </div>
                                                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Total</span>
                                              </div>
                                              <span className="text-base min-[425px]:text-lg font-semibold text-blue-700">${total !== undefined ? total.toFixed(2) : '-'}</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-orange-50/80">
                                <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                  <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-orange-700 flex items-center gap-2">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Order Details
                                  </h3>
                                </div>
                                <div className="border-b border-slate-100 px-4 py-4 md:hidden">
                                  <div className="grid gap-0 md:grid-cols-2">
                                    <div className="space-y-1 border-b border-slate-100 pb-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Order Date</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.todaysdate || '-'}</div>
                                    </div>
                                    <div className="space-y-1 pt-4">
                                      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">UNIQUE ID</div>
                                      <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.customer_unique_id || '-'}</div>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid gap-0 px-4 py-4 md:grid-cols-2">
                                  <div className="space-y-1 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pr-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">MRE Approval</div>
                                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs min-[425px]:text-sm text-slate-800">
                                      <span>{submission.mreused || '-'}</span>
                                      <Link
                                        to={`/admin/model-reports/${submission.mr_id}`}
                                        state={{ from: '/admin' }}
                                        className="inline-flex items-center justify-center rounded-[0.8rem] border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                      >
                                        View MRE
                                      </Link>
                                    </div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:px-4 md:pt-0">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">State</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.state || '-'}</div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">AVV No</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.avvapprovalnumber || '-'}</div>
                                  </div>
                                  <div className="space-y-1 pt-4 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">AVV Email</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">{submission.avvemail || '-'}</div>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-violet-50/80">
                                <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                  <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-violet-700 flex items-center gap-2">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 13l2-5a2 2 0 0 1 2-1h10a2 2 0 0 1 2 1l2 5M5 16v1a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2v-1m6 0v1a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2v-1M7 16h10" />
                                    </svg>
                                    Vehicle Details
                                  </h3>
                                </div>
                                <div className="grid gap-0 px-4 py-4 md:grid-cols-2">
                                  <div className="space-y-1 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pr-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">VIN/CHASSIS CODE</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">{submission.chassisvin || '-'}</div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:px-4 md:pt-0">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Model Code</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">{submission.chassis_code || '-'}</div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Build Date</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.builddate || '-'}</div>
                                  </div>
                                  <div className="space-y-1 pt-4 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4 xl:hidden">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Type</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.vehicle_type || '-'}</div>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-emerald-50/80">
                                <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                                  <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700 flex items-center gap-2">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    Payment Details
                                  </h3>
                                </div>
                                <div className="grid gap-0 px-4 py-4 md:grid-cols-2">
                                  <div className="space-y-1 border-b border-slate-100 pb-4 md:border-b-0 md:border-r md:pr-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Invoice No</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.transactions?.[0]?.invoice_number || '-'}</div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:px-4 md:pt-0">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Payment Date</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800">{submission.transactions?.[0]?.payment_date || '-'}</div>
                                  </div>
                                  <div className="space-y-1 border-b border-slate-100 py-4 md:border-b-0 md:border-t md:border-slate-100 md:pr-4 md:pt-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Reference</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm text-slate-800 break-all">{submission.transactions?.[0]?.reference_no || '-'}</div>
                                  </div>
                                  <div className="space-y-1 pt-4 md:border-l md:border-t md:border-slate-100 md:pl-4 md:pt-4">
                                    <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">Amount</div>
                                    <div className="mt-1 text-xs min-[425px]:text-sm font-medium text-slate-900">{submission.transactions?.[0]?.price ? `$${submission.transactions[0].price}` : '-'}</div>
                                  </div>
                                </div>
                              </div>

                              {canArchiveOrderStatus(submission.status) && (
                                <div className="lg:col-span-2 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleArchiveClick(submission)}
                                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                                  >
                                    Archive order
                                  </button>
                                </div>
                              )}

                              {canRefundOrderStatus(submission.status) && (
                                <div className="lg:col-span-2 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleRefundClick(submission)}
                                    className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                                  >
                                    Refund
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
            </div>
            <PaginationControls />
          </div>

        <ArchiveConfirmModal
          open={!!archiveTarget}
          audience="admin"
          orderLabel={archiveTarget ? `${archiveTarget.chassisvin || 'Order'} · ${archiveTarget.name || ''}`.trim() : ''}
          loading={isArchiving}
          onCancel={() => !isArchiving && setArchiveTarget(null)}
          onConfirm={handleArchiveConfirm}
        />

        <RefundConfirmModal
          open={!!refundTarget}
          vin={refundTarget?.chassisvin || ''}
          status={refundTarget?.status || 'Paid'}
          orderLabel={refundTarget ? `${refundTarget.customer_unique_id || 'Order'} · ${refundTarget.name || ''}`.trim() : ''}
          loading={isRefunding}
          onCancel={() => !isRefunding && setRefundTarget(null)}
          onConfirm={handleRefundConfirm}
        />

        {/* Logout Confirmation Modal */}
        {showLogoutConfirmation && createPortal(
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]">
            <div className="mx-4 w-full max-w-sm rounded-lg border border-slate-300 bg-white p-6 ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]">
              <div className="mb-4 flex items-center">
                <div className="flex-shrink-0">
                  <ArrowRightOnRectangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Are You Sure?</h3>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  Are you sure you want to logout? You will need to sign in again to access your account.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleLogoutCancel}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {toast && (
          <StatusToast title={toast.title} message={toast.message} tone={toast.tone} />
        )}
    </AdminLayout>
  );
};

export default AdminPortal; 
